import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import {
  Wrench, Clock, MapPin, Phone, Star, Calendar, CheckCircle2,
  Loader2, ChevronRight, ChevronLeft, Shield, Sparkles, AlertCircle,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

function PlanCard({ plan, selected, onSelect, lang }) {
  const isPeriodic = plan.id === "periodic_annual" || plan.id === "ownership_transfer";
  return (
    <button
      type="button"
      onClick={() => onSelect(plan)}
      className={`text-start surface rounded-2xl p-6 border-2 transition-all relative ${selected ? "border-[#c9a55c]" : "border-transparent hover:border-[#c9a55c]/40"}`}
      data-testid={`plan-${plan.id}`}
      style={{ borderTopWidth: 4, borderTopColor: plan.color || "#c9a55c" }}
    >
      {plan.popular && (
        <span className="absolute top-2 end-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#c9a55c] text-[#0a0a0a] font-semibold">
          {lang === "ar" ? "الأكثر طلباً" : "Most popular"}
        </span>
      )}
      {plan.recurring && (
        <span className="absolute top-2 end-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#0a0a0a] text-[#c9a55c]">
          {lang === "ar" ? "دوري" : "Recurring"}
        </span>
      )}
      {plan.required_for_transfer && (
        <span className="absolute top-2 end-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-red-600 text-white font-bold">
          {lang === "ar" ? "إلزامي" : "Required"}
        </span>
      )}
      <div className="text-[10px] uppercase tracking-widest font-semibold mb-1.5"
        style={{ color: isPeriodic ? "#8a6f2e" : "#525252" }}>
        {isPeriodic
          ? (lang === "ar" ? "فحص فني دوري (سلامة/فحص)" : "Periodic technical (Salama/Fahas)")
          : (lang === "ar" ? "فحص قبل الشراء" : "Pre-purchase")}
      </div>
      <h3 className="font-display text-xl mb-1">{lang === "ar" ? plan.name_ar : plan.name_en}</h3>
      {plan.required_for_transfer && (
        <div className="text-[11px] text-red-700 font-semibold mb-2 inline-flex items-center gap-1">
          {lang === "ar" ? "(لازم قبل نقل ملكية المركبة)" : "(Required before vehicle ownership transfer)"}
        </div>
      )}
      <div className="text-xs text-[#6b6b6b] mb-3">
        <Clock className="w-3 h-3 inline" /> {plan.duration_min} {lang === "ar" ? "دقيقة" : "min"} · {plan.checkpoints} {lang === "ar" ? "نقطة فحص" : "checkpoints"}
      </div>
      <div className="font-display text-4xl text-[#0a0a0a] mb-1">{formatSAR(plan.price_sar)} <span className="text-sm text-[#8a8a8a]">SAR</span></div>
      <ul className="mt-4 space-y-1.5 text-sm text-[#3a3a3a]">
        {(lang === "ar" ? plan.features_ar : plan.features_en).map((f, i) => (
          <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#8a6f2e] flex-shrink-0 mt-0.5" /> <span>{f}</span></li>
        ))}
      </ul>
    </button>
  );
}

export default function InspectionPage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [centers, setCenters] = useState([]);
  const [step, setStep] = useState(1);
  const [picked, setPicked] = useState({ plan: null, center: null, day: today(), time: null });
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ vehicle_make: "", vehicle_model: "", vehicle_year: new Date().getFullYear(), vehicle_plate: "", contact_name: "", contact_phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    apiClient.get("/inspection/v2/plans").then(({ data }) => setPlans(data.items || []));
  }, []);

  // Reload centers whenever the selected plan changes — backend filters
  // centers by which inspection plans they accept (Salama only accepts
  // periodic_annual/ownership_transfer; CarCheck only accepts basic/full).
  useEffect(() => {
    const params = picked.plan ? { plan_id: picked.plan.id } : {};
    apiClient
      .get("/inspection/v2/centers", { params })
      .then(({ data }) => setCenters(data.items || []));
    // Reset center selection when plan changes — old center may no longer
    // be compatible with the new plan.
    setPicked((p) => ({ ...p, center: null, time: null }));
  }, [picked.plan]);

  useEffect(() => {
    if (!picked.center || !picked.day) return;
    apiClient.get("/inspection/v2/slots", { params: { center_id: picked.center.id, day: picked.day } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]));
  }, [picked.center, picked.day]);

  const cities = Array.from(new Set(centers.map(c => lang === "ar" ? c.city_ar : c.city_en)));
  const [cityFilter, setCityFilter] = useState("");
  const visibleCenters = cityFilter ? centers.filter(c => (lang === "ar" ? c.city_ar : c.city_en) === cityFilter) : centers;

  const submit = async () => {
    if (!user) { navigate("/login", { state: { from: "/inspection" } }); return; }
    setBusy(true);
    try {
      const payload = {
        plan_id: picked.plan.id,
        center_id: picked.center.id,
        day: picked.day,
        time: picked.time,
        ...form,
        vehicle_year: Number(form.vehicle_year),
      };
      const { data } = await apiClient.post("/inspection/v2/appointments", payload);
      setConfirmed(data);
    } catch (e) {
      alert(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (confirmed) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-20 text-center" data-testid="appointment-confirmed">
        <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-12 h-12 text-green-600" />
        </div>
        <h1 className="font-display text-4xl mb-3">{lang === "ar" ? "تم حجز موعدك" : "Appointment booked"}</h1>
        <p className="text-[#6b6b6b] mb-8">{lang === "ar" ? "سنرسل لك رسالة تأكيد بالتفاصيل خلال دقائق." : "We'll text you the confirmation shortly."}</p>
        <div className="surface rounded-2xl p-5 text-start space-y-3 text-sm">
          <Row k={lang === "ar" ? "الباقة" : "Plan"} v={confirmed.plan_name} />
          <Row k={lang === "ar" ? "المركز" : "Center"} v={`${confirmed.center_name} — ${confirmed.center_city}`} />
          <Row k={lang === "ar" ? "التاريخ والوقت" : "When"} v={`${confirmed.day} · ${confirmed.time}`} />
          <Row k={lang === "ar" ? "المركبة" : "Vehicle"} v={`${confirmed.vehicle_year} ${confirmed.vehicle_make} ${confirmed.vehicle_model}`} />
          {confirmed.next_due_at && <Row k={lang === "ar" ? "الفحص القادم" : "Next due"} v={confirmed.next_due_at} highlight />}
          <div className="pt-3 border-t border-[#0a0a0a]/[0.06] flex justify-between font-display text-xl">
            <span>{lang === "ar" ? "السعر" : "Price"}</span>
            <span>{formatSAR(confirmed.price_sar)} SAR</span>
          </div>
        </div>
        <button onClick={() => navigate("/dashboard")} className="btn-primary mx-auto mt-8" data-testid="goto-dashboard-from-appt">
          {lang === "ar" ? "إلى لوحتي" : "My dashboard"}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-10" data-testid="inspection-page">
      <div className="mb-8">
        <div className="overline overline-gold mb-2">{lang === "ar" ? "فحص المركبات" : "Vehicle inspection"}</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-[-0.02em] font-medium mb-3">{lang === "ar" ? "احجز فحص مركبتك في دقيقة" : "Book your inspection in a minute"}</h1>
        <p className="text-[#6b6b6b] max-w-2xl">{lang === "ar" ? "مراكز فحص معتمدة في 6 مدن، فنيون خبراء، تقرير مفصّل بـ PDF." : "Certified centers across 6 cities, expert technicians, detailed PDF report."}</p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto">
        {[
          { n: 1, ar: "اختر الباقة", en: "Plan" },
          { n: 2, ar: "اختر المركز", en: "Center" },
          { n: 3, ar: "اختر الموعد", en: "Slot" },
          { n: 4, ar: "البيانات", en: "Details" },
        ].map(s => (
          <button
            key={s.n}
            type="button"
            onClick={() => s.n < step && setStep(s.n)}
            className={`px-4 py-2 text-xs uppercase tracking-widest rounded-full whitespace-nowrap ${step === s.n ? "bg-[#0a0a0a] text-[#f1ecdf]" : s.n < step ? "text-[#8a6f2e]" : "text-[#8a8a8a]"}`}
          >
            {s.n}. {lang === "ar" ? s.ar : s.en}
          </button>
        ))}
      </div>

      {/* Step 1: Plan */}
      {step === 1 && (
        <div className="space-y-8" data-testid="step-1-plans">
          {/* Pre-purchase plans (private workshops for buyers) */}
          <div>
            <div className="mb-3">
              <div className="overline overline-gold mb-1">{lang === "ar" ? "1. فحص قبل الشراء" : "1. Pre-purchase inspection"}</div>
              <p className="text-sm text-[#525252] max-w-2xl">
                {lang === "ar"
                  ? "للمشترين الذين يريدون الاطمئنان على حالة السيارة قبل الشراء. تُقدّم من مراكز خدمة سريعة وميكانيكيين معتمدين."
                  : "For buyers who want peace of mind before purchase. Provided by quick-service centers and certified mechanics."}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.filter(p => !["periodic_annual", "ownership_transfer"].includes(p.id)).map(p => (
                <PlanCard key={p.id} plan={p} selected={picked.plan?.id === p.id} onSelect={(plan) => setPicked({ ...picked, plan })} lang={lang} />
              ))}
            </div>
          </div>

          {/* Periodic / govt-mandated inspections (Salama, Fahas) */}
          <div>
            <div className="mb-3">
              <div className="overline overline-gold mb-1">{lang === "ar" ? "2. الفحص الفني الدوري" : "2. Periodic technical inspection"}</div>
              <p className="text-sm text-[#525252] max-w-2xl leading-relaxed">
                {lang === "ar" ? (
                  <>إلزامي حسب نظام المرور السعودي قبل نقل ملكية السيارة أو تجديد الاستمارة. متاح في <strong>شركة سلامة</strong> و<strong>فحص</strong> و<strong>المروج</strong>.</>
                ) : (
                  <>Mandatory under Saudi traffic law before ownership transfer or registration renewal. Provided by <strong>Salama</strong>, <strong>Fahas</strong> and <strong>Al-Morooj</strong>.</>
                )}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {plans.filter(p => ["periodic_annual", "ownership_transfer"].includes(p.id)).map(p => (
                <PlanCard key={p.id} plan={p} selected={picked.plan?.id === p.id} onSelect={(plan) => setPicked({ ...picked, plan })} lang={lang} />
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={() => setStep(2)} disabled={!picked.plan} className="btn-primary disabled:opacity-50" data-testid="step-1-next">
              {lang === "ar" ? "التالي" : "Next"} <ChevronLeft className="w-4 h-4 rtl:hidden" /><ChevronRight className="w-4 h-4 ltr:hidden" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Center */}
      {step === 2 && (
        <div className="space-y-5">
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setCityFilter("")} className={`pill ${!cityFilter ? "pill-accent" : ""}`}>{lang === "ar" ? "كل المدن" : "All cities"}</button>
            {cities.map(c => <button key={c} onClick={() => setCityFilter(c)} className={`pill ${cityFilter === c ? "pill-accent" : ""}`}>{c}</button>)}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleCenters.map(c => (
              <button key={c.id} type="button" onClick={() => setPicked({ ...picked, center: c })} className={`text-start surface rounded-2xl overflow-hidden border-2 transition-all ${picked.center?.id === c.id ? "border-[#c9a55c]" : "border-transparent hover:border-[#c9a55c]/40"}`} data-testid={`center-${c.id}`}>
                <div className="relative">
                  <img src={c.image} alt="" className="w-full h-32 object-cover" />
                  {c.official && (
                    <span
                      className="absolute top-2 start-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold inline-flex items-center gap-1"
                      style={{ backgroundColor: "#0a0a0a", color: "#d4ae6a", border: "1px solid #c9a55c" }}
                    >
                      <Shield className="w-3 h-3" /> {lang === "ar" ? "معتمد" : "Official"}
                    </span>
                  )}
                  {c.mobile && (
                    <span
                      className="absolute top-2 end-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold"
                      style={{ backgroundColor: "#c9a55c", color: "#0a0a0a" }}
                    >
                      {lang === "ar" ? "متنقل" : "Mobile"}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {c.operator_ar && (
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-[#8a6f2e] mb-0.5 truncate" data-testid={`center-operator-${c.id}`}>
                          {lang === "ar" ? c.operator_ar : c.operator_en}
                        </div>
                      )}
                      <h3 className="font-display text-base sm:text-lg leading-tight">{lang === "ar" ? c.name_ar : c.name_en}</h3>
                    </div>
                    <span className="text-xs text-[#8a6f2e] inline-flex items-center gap-0.5 shrink-0"><Star className="w-3 h-3 fill-current" /> {c.rating}</span>
                  </div>
                  <div className="text-xs text-[#6b6b6b] mt-2 space-y-1">
                    <div className="inline-flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /> <span>{lang === "ar" ? c.address_ar : c.address_en || c.address_ar}, {lang === "ar" ? c.city_ar : c.city_en}</span></div>
                    <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-ghost"><ChevronRight className="w-4 h-4 rtl:hidden" /><ChevronLeft className="w-4 h-4 ltr:hidden" /> {lang === "ar" ? "السابق" : "Back"}</button>
            <button onClick={() => setStep(3)} disabled={!picked.center} className="btn-primary disabled:opacity-50" data-testid="step-2-next">{lang === "ar" ? "التالي" : "Next"} <ChevronLeft className="w-4 h-4 rtl:hidden" /><ChevronRight className="w-4 h-4 ltr:hidden" /></button>
          </div>
        </div>
      )}

      {/* Step 3: Slot */}
      {step === 3 && (
        <div className="space-y-5">
          <div className="surface rounded-xl p-5">
            <label className="text-xs text-[#6b6b6b] uppercase tracking-widest block mb-2"><Calendar className="w-3 h-3 inline" /> {lang === "ar" ? "اختر التاريخ" : "Pick a date"}</label>
            <input type="date" min={today()} value={picked.day} onChange={(e) => setPicked({ ...picked, day: e.target.value, time: null })} className="input-dark max-w-xs" data-testid="appt-date" />
          </div>
          <div>
            <div className="text-xs text-[#6b6b6b] uppercase tracking-widest mb-3">{lang === "ar" ? "المواعيد المتاحة" : "Available time slots"}</div>
            {slots.length === 0 ? (
              <div className="text-sm text-[#6b6b6b]"><Loader2 className="w-4 h-4 animate-spin inline" /> {lang === "ar" ? "تحميل..." : "Loading..."}</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                {slots.map(s => (
                  <button
                    key={s.time}
                    type="button"
                    disabled={!s.available}
                    onClick={() => setPicked({ ...picked, time: s.time })}
                    className={`px-3 py-3 rounded-lg border-2 text-sm transition-all ${picked.time === s.time ? "border-[#c9a55c] bg-[#c9a55c]/10 text-[#5a4615] font-semibold" : s.available ? "border-[#0a0a0a]/[0.08] hover:border-[#c9a55c]/40" : "border-[#0a0a0a]/[0.05] opacity-40 cursor-not-allowed line-through"}`}
                    data-testid={`slot-${s.time}`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="btn-ghost"><ChevronRight className="w-4 h-4 rtl:hidden" /><ChevronLeft className="w-4 h-4 ltr:hidden" /> {lang === "ar" ? "السابق" : "Back"}</button>
            <button onClick={() => setStep(4)} disabled={!picked.time} className="btn-primary disabled:opacity-50" data-testid="step-3-next">{lang === "ar" ? "التالي" : "Next"} <ChevronLeft className="w-4 h-4 rtl:hidden" /><ChevronRight className="w-4 h-4 ltr:hidden" /></button>
          </div>
        </div>
      )}

      {/* Step 4: Details */}
      {step === 4 && (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-4">
            <h2 className="font-display text-2xl mb-2">{lang === "ar" ? "بيانات المركبة والتواصل" : "Vehicle & contact details"}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={lang === "ar" ? "الماركة" : "Make"}>
                <input className="input-dark w-full" value={form.vehicle_make} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} required data-testid="appt-make" placeholder="Toyota" />
              </Field>
              <Field label={lang === "ar" ? "الموديل" : "Model"}>
                <input className="input-dark w-full" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} required data-testid="appt-model" placeholder="Camry" />
              </Field>
              <Field label={lang === "ar" ? "سنة الصنع" : "Year"}>
                <input className="input-dark w-full" type="number" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} required data-testid="appt-year" />
              </Field>
              <Field label={lang === "ar" ? "رقم اللوحة (اختياري)" : "Plate (optional)"}>
                <input className="input-dark w-full" value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value })} data-testid="appt-plate" />
              </Field>
              <Field label={lang === "ar" ? "اسمك" : "Your name"}>
                <input className="input-dark w-full" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} required data-testid="appt-name" />
              </Field>
              <Field label={lang === "ar" ? "رقم الجوال" : "Phone"}>
                <input className="input-dark w-full" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+966 5x xxx xxxx" required data-testid="appt-phone" />
              </Field>
              <Field label={lang === "ar" ? "ملاحظات (اختياري)" : "Notes (optional)"} className="sm:col-span-2">
                <textarea className="input-dark w-full min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="appt-notes" />
              </Field>
            </div>
            <div className="flex justify-between pt-4">
              <button onClick={() => setStep(3)} className="btn-ghost"><ChevronRight className="w-4 h-4 rtl:hidden" /><ChevronLeft className="w-4 h-4 ltr:hidden" /> {lang === "ar" ? "السابق" : "Back"}</button>
              <button onClick={submit} disabled={busy || !form.vehicle_make || !form.vehicle_model || !form.contact_name || !form.contact_phone} className="btn-primary disabled:opacity-50" data-testid="appt-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {lang === "ar" ? "تأكيد الحجز" : "Confirm booking"}
              </button>
            </div>
          </div>

          {/* Summary */}
          <aside className="surface rounded-2xl p-5 h-fit lg:sticky lg:top-24 space-y-3">
            <div className="overline overline-gold">{lang === "ar" ? "ملخص الحجز" : "Booking summary"}</div>
            <div className="space-y-2 text-sm">
              <Row k={lang === "ar" ? "الباقة" : "Plan"} v={lang === "ar" ? picked.plan?.name_ar : picked.plan?.name_en} />
              <Row k={lang === "ar" ? "المركز" : "Center"} v={lang === "ar" ? picked.center?.name_ar : picked.center?.name_en} />
              <Row k={lang === "ar" ? "التاريخ" : "Date"} v={picked.day} />
              <Row k={lang === "ar" ? "الوقت" : "Time"} v={picked.time} />
              <Row k={lang === "ar" ? "المدة" : "Duration"} v={`${picked.plan?.duration_min} ${lang === "ar" ? "دقيقة" : "min"}`} />
            </div>
            <div className="pt-3 border-t border-[#0a0a0a]/[0.06] flex justify-between font-display text-2xl">
              <span>{lang === "ar" ? "الإجمالي" : "Total"}</span>
              <span>{formatSAR(picked.plan?.price_sar || 0)}</span>
            </div>
            <div className="text-xs text-[#6b6b6b] inline-flex items-start gap-1.5 pt-3">
              <Shield className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#8a6f2e]" />
              <span>{lang === "ar" ? "الدفع عند الحضور — يمكنك الإلغاء حتى قبل الموعد بـ 6 ساعات." : "Pay on arrival — cancel up to 6h before."}</span>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs text-[#6b6b6b] block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[#8a8a8a] text-xs">{k}</dt>
      <dd className={`font-medium text-end ${highlight ? "text-[#8a6f2e]" : "text-[#0a0a0a]"}`}>{v || "—"}</dd>
    </div>
  );
}
